import styles from '~/modules/game-engine/drawing/styles';
import { cn } from '~/utils/cn';

import { BreakState } from './utils';

/**
 * Deliberately not the intro's ring: that one is paired with an action ("press Enter to skip"),
 * and a break cannot be skipped, so giving it the same shape would invite a press that does
 * nothing. It sits where the lyric would be, and the next line stays on screen to read ahead.
 */
export function BreakAnnouncement({
  state,
  next,
}: {
  state: BreakState;
  /** Who takes the microphone when this break ends, when the break is itself the hand-over. */
  next: { name: string; color: string } | null;
}) {
  return (
    <div
      className={cn(
        'stroke-text flex h-14 items-center justify-center gap-[1.125rem] tracking-[.06em] uppercase',
        state.ready ? 'text-active text-[2.125rem] font-black' : 'text-[1.875rem] font-bold text-white/45',
      )}
      data-test="break-announcement"
      data-break-ready={state.ready}>
      {state.ready ? 'Get ready' : 'Break'}
      <span className={cn('tabular-nums', state.ready ? 'text-white' : 'text-white/70')}>{state.secondsLeft} s</span>
      {next && (
        <>
          <span className="opacity-50">&rarr;</span>
          <span className="ph-no-capture" style={{ color: next.color }}>
            {next.name}
          </span>
        </>
      )}
    </div>
  );
}

/** The same 4px geometry the hand-over's bar uses, at the other edge of the band. */
export function BreakProgress({ state, next }: { state: BreakState; next: { color: string } | null }) {
  return (
    <div
      className="absolute inset-x-0 bottom-0 h-1 origin-left"
      data-test="break-progress"
      style={{
        background: state.ready ? styles.colors.text.active : (next?.color ?? 'rgba(255,255,255,0.3)'),
        transform: `scaleX(${state.progress})`,
      }}
    />
  );
}

/** What the other band shows while the song rests: no lyric, and not an empty box either. */
export function BreakRest() {
  return (
    <div className="stroke-text flex h-14 items-center justify-center text-[2.125rem] font-bold text-white/35">
      &#9834;
    </div>
  );
}

/** The video runs on after the last note, and until now the bands just emptied out. */
export function SingingOverNotice() {
  return (
    <div
      className="stroke-text flex h-14 items-center justify-center text-[1.875rem] font-bold tracking-[.06em] text-white/45 uppercase"
      data-test="singing-over">
      That&rsquo;s everything
    </div>
  );
}
